import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { ArrowRight, ArrowLeft, Target, Trophy } from "lucide-react";
import { api } from "../lib/api";
import { BrandMark, BrandWordmark } from "./LandingPage";
import { fmtPct, fmtRatio, fmtDays, fmtCurrency, fmtNumber } from "../lib/format";

const REGION_NAMES = {
  US: "United States", CA: "Canada", UK: "United Kingdom", EU: "Eurozone",
  AU: "Australia / New Zealand", IN: "India", SG: "Singapore", AE: "UAE / GCC",
  ZA: "South Africa", NG: "Nigeria", KE: "Kenya / East Africa", BR: "Brazil",
  MX: "Mexico / LATAM", JP: "Japan", GLOBAL: "Global",
};

function fmtKpi(v, fmt) {
  if (v === null || v === undefined) return "—";
  switch (fmt) {
    case "pct": return fmtPct(v, 1);
    case "ratio": return fmtRatio(v);
    case "days": return fmtDays(v);
    case "money": return fmtCurrency(v);
    case "number":
      if (v >= 999) return "12+ mo";
      return fmtNumber(v, 0);
    default: return String(v);
  }
}

export default function SegmentPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!slug) return;
    api
      .get(`/leaderboard/segment/${slug}`)
      .then((r) => {
        setData(r.data);
        const regionName = REGION_NAMES[r.data.region] || r.data.region;
        const desc = `Typical financial ratios and operating KPIs for ${r.data.industry_label} businesses in ${regionName}. Median margins, DSCR, debt levels, and the metrics operators in this sector actually watch.`;
        document.title = `${r.data.industry_label} financial benchmarks (${regionName}) — CFO Grade`;
        const meta = document.querySelector('meta[name="description"]');
        if (meta) meta.setAttribute("content", desc);

        // Update OG/Twitter tags so Reddit/Slack/etc preview-cards reflect the page
        upsertMeta("property", "og:title", `${r.data.industry_label} financial benchmarks — CFO Grade`);
        upsertMeta("property", "og:description", desc);
        upsertMeta("name", "twitter:title", `${r.data.industry_label} financial benchmarks — CFO Grade`);
        upsertMeta("name", "twitter:description", desc);

        // Canonical link
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) {
          canonical = document.createElement("link");
          canonical.setAttribute("rel", "canonical");
          document.head.appendChild(canonical);
        }
        canonical.setAttribute("href", `${window.location.origin}/leaderboard/${slug}`);

        // JSON-LD: Dataset + WebPage so Google can show rich results
        injectJsonLd(`segment-${slug}`, buildSegmentJsonLd(r.data, regionName, slug));
      })
      .catch((e) => setError(e?.response?.data?.detail || "Segment not found."));
    return () => {
      removeJsonLd(`segment-${slug}`);
    };
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <p className="font-serif-display text-3xl text-[#1F382E]">Segment not found.</p>
          <p className="text-[#5C665D] mt-3 leading-relaxed">{error}</p>
          <Link to="/leaderboard">
            <Button className="mt-6 bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0]">
              Back to Leaderboard
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center">
        <p className="text-[#5C665D]">Loading…</p>
      </div>
    );
  }

  const regionName = REGION_NAMES[data.region] || data.region;
  const cmps = data.benchmark?.comparisons || {};

  return (
    <div className="min-h-screen bg-[#F7F5F0] bg-grain relative">
      <Header />
      <main className="max-w-5xl mx-auto px-6 sm:px-10 py-10 sm:py-14">
        <div className="fade-up">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#5C665D] border border-[#E5E0D8] bg-white/70 rounded-full px-3 py-1.5">
            <Trophy size={11} strokeWidth={1.5} className="text-[#C26D5C]" />
            Industry benchmark · {regionName}
          </span>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#1F382E] leading-[1.05] tracking-tight mt-6">
            {data.industry_label} financials.
          </h1>
          <p className="text-base sm:text-lg text-[#5C665D] leading-relaxed mt-5 max-w-3xl">
            {data.industry_notes} Below: the typical operating ranges that bankers, accountants
            and smart operators in this sector watch — plus what each one is telling you about
            the health of the business.
          </p>
        </div>

        <section className="mt-12">
          <div className="flex items-center gap-2 mb-2">
            <Target size={16} strokeWidth={1.5} className="text-[#C26D5C]" />
            <p className="text-xs uppercase tracking-[0.22em] text-[#5C665D]">
              Industry-specific KPIs
            </p>
          </div>
          <h2 className="font-serif-display text-3xl text-[#1F382E] tracking-tight">
            What to watch first.
          </h2>
          <div className="mt-6 space-y-5">
            {data.industry_kpis.map((kpi) => (
              <Card key={kpi.key} className="bg-white border-[#E5E0D8] shadow-none">
                <CardHeader className="pb-3">
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <CardTitle className="font-serif-display text-xl text-[#1F382E]">
                      {kpi.label}
                    </CardTitle>
                    {kpi.range && !(kpi.fmt === "money" && kpi.range[0] === 0 && kpi.range[1] === 0 && kpi.range[2] === 0) && (
                      <span className="text-sm tabular-nums text-[#5C665D]">
                        Typical {fmtKpi(kpi.range[0], kpi.fmt)} – {fmtKpi(kpi.range[2], kpi.fmt)}
                        {" · "}
                        median <strong className="text-[#1F382E]">{fmtKpi(kpi.range[1], kpi.fmt)}</strong>
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {kpi.what_it_means && (
                    <p className="text-sm text-[#1A1A1A] leading-relaxed">{kpi.what_it_means}</p>
                  )}
                  {kpi.improve_levers && kpi.improve_levers.length > 0 && (
                    <div className="mt-4">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C665D] mb-2">
                        How operators move the needle
                      </p>
                      <ul className="space-y-1.5">
                        {kpi.improve_levers.map((l, i) => (
                          <li key={i} className="text-sm flex gap-2 text-[#1A1A1A]">
                            <span className="mt-2 h-1 w-1 rounded-full bg-[#C26D5C] shrink-0" />
                            <span>{l}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section className="mt-16">
          <p className="text-xs uppercase tracking-[0.22em] text-[#5C665D]">Universal ratios</p>
          <h2 className="font-serif-display text-3xl text-[#1F382E] tracking-tight mt-2">
            What lenders and acquirers benchmark.
          </h2>
          <p className="text-sm text-[#5C665D] mt-2 leading-relaxed">
            Cross-industry ratios that apply to {data.industry_label} just like every other
            business — typical ranges shown for {regionName}.
          </p>
          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            {[
              ["gross_margin", "Gross margin", "pct"],
              ["ebitda_margin", "EBITDA margin", "pct"],
              ["net_margin", "Net margin", "pct"],
              ["current_ratio", "Current ratio", "ratio"],
              ["dscr", "DSCR", "ratio"],
              ["debt_to_ebitda", "Debt / EBITDA", "ratio"],
              ["interest_coverage", "Interest coverage", "ratio"],
              ["dso", "DSO", "days"],
              ["dio", "DIO", "days"],
              ["cash_conversion_cycle", "Cash conv. cycle", "days"],
            ].map(([key, label, fmt]) => {
              const c = cmps[key];
              if (!c) return null;
              return (
                <div
                  key={key}
                  className="bg-white border border-[#E5E0D8] rounded-lg p-4 flex items-baseline justify-between"
                >
                  <span className="text-sm text-[#1A1A1A]">{label}</span>
                  <span className="text-sm tabular-nums text-[#5C665D]">
                    {fmtKpi(c.low, fmt)} – {fmtKpi(c.high, fmt)}
                    {" · "}
                    <strong className="text-[#1F382E]">median {fmtKpi(c.median, fmt)}</strong>
                  </span>
                </div>
              );
            })}
          </div>
        </section>

        {data.community_medians && (
          <section className="mt-16 bg-white border border-[#E5E0D8] rounded-2xl p-8">
            <p className="text-xs uppercase tracking-[0.22em] text-[#C26D5C]">Community data</p>
            <h2 className="font-serif-display text-2xl text-[#1F382E] tracking-tight mt-2">
              From {data.n} {data.industry_label} operators in {regionName}.
            </h2>
            <p className="text-sm text-[#5C665D] mt-2 leading-relaxed">
              Anonymous medians, contributed by businesses just like yours.
            </p>
            <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(data.community_medians).map(([k, v]) => (
                <div key={k} className="text-sm flex items-baseline justify-between">
                  <span className="text-[#5C665D]">{k.replace(/_/g, " ")}</span>
                  <span className="tabular-nums text-[#1F382E]">{v}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="mt-20 bg-[#1F382E] rounded-2xl p-10 sm:p-14 text-[#F7F5F0] relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-30 pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 20%, #C26D5C 0%, transparent 40%), radial-gradient(circle at 80% 80%, #D4C9B6 0%, transparent 40%)",
            }}
          />
          <div className="relative max-w-3xl">
            <h2 className="font-serif-display text-4xl sm:text-5xl leading-tight">
              See how your {data.industry_label.toLowerCase()} stacks up.
            </h2>
            <p className="text-[#D4C9B6] mt-4 leading-relaxed max-w-xl">
              Drop in your numbers — five minutes — and get your CFO Grade, full ratio
              dashboard, and the industry-specific recommendations that will actually move the
              needle for your business.
            </p>
            <Link to="/analyze">
              <Button className="mt-8 bg-[#C26D5C] hover:bg-[#a85a4b] text-white rounded-md h-12 px-7 transition-all duration-300">
                Run my analysis <ArrowRight size={16} strokeWidth={1.5} className="ml-2" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function Header() {
  return (
    <header className="max-w-5xl mx-auto px-6 sm:px-10 pt-8 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2.5">
        <BrandWordmark size="md" />
      </Link>
      <Link to="/leaderboard">
        <Button variant="ghost" className="text-[#5C665D] hover:bg-[#D4C9B6]/40">
          <ArrowLeft size={14} strokeWidth={1.5} className="mr-2" />
          All segments
        </Button>
      </Link>
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[#E5E0D8] py-10 max-w-5xl mx-auto px-6 sm:px-10 mt-12 text-sm text-[#5C665D] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <span>© {new Date().getFullYear()} CFO Grade. Educational insights for business owners — not financial advice.</span>
      <Link to="/disclaimer" className="underline underline-offset-2 hover:text-[#1F382E] transition-colors">Full terms</Link>
    </footer>
  );
}

// ---------- SEO helpers ----------
function upsertMeta(attr, key, value) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function injectJsonLd(id, payload) {
  removeJsonLd(id);
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.id = `ldjson-${id}`;
  s.text = JSON.stringify(payload);
  document.head.appendChild(s);
}

function removeJsonLd(id) {
  const existing = document.getElementById(`ldjson-${id}`);
  if (existing) existing.parentNode.removeChild(existing);
}

function buildSegmentJsonLd(segment, regionName, slug) {
  const url = `${window.location.origin}/leaderboard/${slug}`;
  const variables = (segment.industry_kpis || []).slice(0, 5).map((k) => ({
    "@type": "PropertyValue",
    name: k.label,
    value: k.range ? `${k.range[0]} – ${k.range[2]} (median ${k.range[1]})` : undefined,
    description: k.explainer,
  }));
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": url,
        url,
        name: `${segment.industry_label} financial benchmarks — CFO Grade`,
        description: `Typical financial ratios and operating KPIs for ${segment.industry_label} businesses in ${regionName}.`,
        isPartOf: { "@type": "WebSite", name: "CFO Grade", url: window.location.origin },
        about: { "@type": "Thing", name: segment.industry_label },
        breadcrumb: {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "CFO Grade", item: window.location.origin },
            { "@type": "ListItem", position: 2, name: "Leaderboard", item: `${window.location.origin}/leaderboard` },
            { "@type": "ListItem", position: 3, name: segment.industry_label, item: url },
          ],
        },
      },
      {
        "@type": "Dataset",
        name: `${segment.industry_label} financial benchmarks (${regionName})`,
        description: `Median financial ratios and tailored KPIs for ${segment.industry_label} operators in ${regionName}.`,
        url,
        keywords: [
          segment.industry_label,
          regionName,
          "financial benchmarks",
          "EBITDA margin",
          "DSCR",
          "industry KPIs",
        ],
        creator: { "@type": "Organization", name: "CFO Grade" },
        license: "https://cfograde.com/disclaimer",
        variableMeasured: variables,
      },
    ],
  };
}

